/**
 * Piezas compartidas por las pantallas de atención de un turno.
 *
 * POR QUÉ EXISTE
 * Vivían sueltas dentro de VistaDetalladaAtencion.js, que era el único que las usaba. Cuando la
 * atención de los turnos de cobro se separó a su propio componente (VistaDetalladaAtencionCobro),
 * las dos pantallas pasaron a necesitar la misma grilla de datos y los mismos formatos: la opción
 * era duplicarlas o subirlas acá. Son funciones puras y un componente de presentación de seis
 * líneas, sin estado ni red, así que compartirlas no acopla nada.
 *
 * Lo que NO está acá, a propósito: TRAMITES_CONFIG (arrastra los once formularios de trámite) y
 * resolverClienteAsociadoTurno (hace red). Los sigue teniendo VistaDetalladaAtencion.js, que es
 * el único que los usa.
 */
import { useEffect, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { normalizarParaComparar } from '../../utils/textoComparable';

/** `YYYY-MM-DD` (o ISO con hora) → `DD/MM/YYYY`. */
export function formatearFecha(fechaStr) {
    if (!fechaStr) return '-';
    const partes = String(fechaStr).split('T')[0].split('-');
    if (partes.length !== 3) return fechaStr;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

/** `HH:MM:SS` → `HH:MM`. */
export function formatearHorario(horario) {
    if (!horario) return '-';
    return String(horario).substring(0, 5);
}

/** Fecha ISO con hora → `DD/MM/YYYY HH:MM`. */
export function formatearFechaHora(valor) {
    if (!valor) return '-';
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) return '-';
    const horario = fecha.toTimeString().substring(0, 5);
    return `${formatearFecha(valor)} ${horario}`;
}

/** `PLAN_PAGO` → `Plan Pago`. */
export function formatearTipoTramite(tipo) {
    if (!tipo) return 'General';
    return String(tipo)
        .toLowerCase()
        .split('_')
        .map((parte) => parte.charAt(0).toUpperCase() + parte.slice(1))
        .join(' ');
}

// Una celda de la grilla de datos (etiqueta + valor). Los campos sin dato se muestran en gris
// claro con "Sin dato" en vez de "-", para que se distingan de un valor real a simple vista.
export const CampoDato = ({ etiqueta, valor, muted = false }) => (
    <div className="atn-field">
        <span className="atn-field-label">{etiqueta}</span>
        <span className={`atn-field-value${muted ? ' atn-field-value--muted' : ''}`}>{valor}</span>
    </div>
);

/**
 * Combobox sobre un catálogo de Datos Maestros: se escribe para filtrar y, si lo tipeado no
 * está en el catálogo, se da de alta en la base.
 *
 * POR QUÉ EXISTE
 * El motivo de no avance y el resultado salen de catálogos administrables que ya tienen
 * decenas de filas: un desplegable largo obliga a buscar con la vista mientras hay alguien
 * esperando del otro lado del mostrador. Escribiendo se llega en dos teclas. Y cuando el caso
 * todavía no está en el catálogo, la atención no se frena esperando que un supervisor lo
 * cargue: se crea acá y queda para las próximas.
 *
 * El alta escribe en el catálogo real (mismo endpoint que el ABM de Datos Maestros), así que
 * lo que se cargue acá aparece después en Administración y en los reportes.
 *
 * Con `permitirAlta={false}` es un selector cerrado: solo se puede elegir una opción existente
 * (el resultado de la atención funciona así; sus opciones se administran en Datos Maestros).
 */
export const SelectorCatalogoConAlta = ({ value, onChange, opciones, placeholder, onCrear, permitirAlta = true }) => {
    const [texto, setTexto] = useState(value || '');
    const [abierto, setAbierto] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');
    const contenedorRef = useRef(null);
    // Elegir una opción de la lista dispara el blur del input; sin esta bandera el blur
    // interpretaría el texto a medio tipear como "valor nuevo" y lo daría de alta.
    const eligiendoRef = useRef(false);
    // Último valor que este combo le pasó al padre. Sirve para distinguir un cambio de `value` que
    // viene de afuera (se muestra) de uno que acabamos de emitir mientras se escribe (no se pisa lo
    // que el operador está tipeando).
    const ultimoEmitidoRef = useRef(value || '');

    useEffect(() => {
        if ((value || '') === ultimoEmitidoRef.current) return;
        ultimoEmitidoRef.current = value || '';
        setTexto(value || '');
    }, [value]);

    const emitir = (nuevoValor) => {
        ultimoEmitidoRef.current = nuevoValor;
        onChange(nuevoValor);
    };

    useEffect(() => {
        const alClickearAfuera = (e) => {
            if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
        };
        document.addEventListener('mousedown', alClickearAfuera);
        return () => document.removeEventListener('mousedown', alClickearAfuera);
    }, []);

    const buscado = normalizarParaComparar(texto);
    const filtradas = buscado
        ? opciones.filter((o) => normalizarParaComparar(o.nombre).includes(buscado))
        : opciones;
    const exacta = opciones.find((o) => normalizarParaComparar(o.nombre) === buscado);
    const puedeCrear = permitirAlta && Boolean(texto.trim()) && !exacta;

    const elegir = (nombre) => {
        eligiendoRef.current = true;
        setTexto(nombre);
        setError('');
        setAbierto(false);
        emitir(nombre);
        setTimeout(() => { eligiendoRef.current = false; }, 300);
    };

    const limpiar = () => {
        setTexto('');
        setError('');
        setAbierto(false);
        emitir('');
    };

    // Lo tipeado que coincide con una opción queda elegido en el momento, sin esperar al blur. El
    // blur se resuelve 200 ms después y un clic directo en "Finalizar" llegaba antes, con el valor
    // viejo: el resultado escrito se perdía sin aviso. Si lo tipeado deja de coincidir, se suelta
    // la opción: el valor tiene que ser lo que se ve.
    const alEscribir = (nuevoTexto) => {
        setTexto(nuevoTexto);
        setAbierto(true);
        setError('');
        const buscadoNuevo = normalizarParaComparar(nuevoTexto);
        const coincidencia = opciones.find((o) => normalizarParaComparar(o.nombre) === buscadoNuevo);
        const nuevoValor = coincidencia ? coincidencia.nombre : '';
        if (nuevoValor !== (value || '')) emitir(nuevoValor);
    };

    const crear = async () => {
        const nombre = texto.trim();
        if (!nombre) return;
        // Si ya existe (ignorando acentos y mayúsculas) se elige la que está, en vez de
        // pedirle al backend que falle por nombre duplicado.
        if (exacta) { elegir(exacta.nombre); return; }
        eligiendoRef.current = true;
        setGuardando(true);
        setError('');
        try {
            const creado = await onCrear(nombre);
            setTexto(creado.nombre);
            setAbierto(false);
            emitir(creado.nombre);
        } catch (e) {
            setError(
                e?.response?.data?.message
                || e?.response?.data?.mensaje
                || 'No se pudo guardar. Probá de nuevo o cargalo desde Datos Maestros.'
            );
        } finally {
            setGuardando(false);
            setTimeout(() => { eligiendoRef.current = false; }, 300);
        }
    };

    // Al salir del campo se resuelve lo tipeado: si coincide con una opción se toma esa (con su
    // nombre tal cual está en el catálogo), si es texto nuevo se da de alta (solo si se permite) y
    // si quedó vacío se limpia la selección. En un selector cerrado, lo que no coincide se descarta.
    const alSalir = () => {
        setTimeout(() => {
            if (eligiendoRef.current || guardando) return;
            const nombre = texto.trim();
            if (!nombre) { if (value) limpiar(); return; }
            if (exacta) { if (exacta.nombre !== value || texto !== exacta.nombre) elegir(exacta.nombre); return; }
            if (!permitirAlta) {
                setTexto(value || '');
                setAbierto(false);
                setError('Elegí una de las opciones de la lista.');
                return;
            }
            if (nombre.length > 2) crear();
        }, 200);
    };

    const alTeclear = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (exacta) elegir(exacta.nombre);
            else if (filtradas.length === 1) elegir(filtradas[0].nombre);
            else if (puedeCrear) crear();
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            setAbierto(false);
            setTexto(value || '');
        }
    };

    return (
        <div className="atn-combo" ref={contenedorRef}>
            <div className="atn-combo-campo">
                <Form.Control
                    type="text"
                    className="atn-combo-input"
                    value={texto}
                    maxLength={150}
                    placeholder={placeholder}
                    disabled={guardando}
                    onChange={(e) => alEscribir(e.target.value)}
                    onFocus={() => setAbierto(true)}
                    onBlur={alSalir}
                    onKeyDown={alTeclear}
                />
                {guardando && <Spinner animation="border" size="sm" className="atn-combo-spinner" />}
                {!guardando && texto && (
                    <button type="button" className="atn-combo-limpiar" onClick={limpiar} title="Quitar selección">
                        <i className="bi bi-x-lg"></i>
                    </button>
                )}
            </div>

            {abierto && (filtradas.length > 0 || puedeCrear) && (
                <div className="atn-combo-lista">
                    {filtradas.map((o) => (
                        <button
                            key={o.id}
                            type="button"
                            className={`atn-combo-opcion${o.nombre === value ? ' atn-combo-opcion--activa' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => elegir(o.nombre)}
                        >
                            {o.nombre}
                        </button>
                    ))}
                    {puedeCrear && (
                        <button
                            type="button"
                            className="atn-combo-opcion atn-combo-opcion--nueva"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={crear}
                        >
                            <i className="bi bi-plus-circle me-2"></i>
                            Agregar «{texto.trim()}» al catálogo
                        </button>
                    )}
                </div>
            )}

            {error
                ? <p className="atn-alta-catalogo-error">{error}</p>
                : puedeCrear ? (
                    <p className="atn-form-hint" style={{ marginTop: '6px', marginBottom: 0 }}>
                        No está en la lista: se guarda en el catálogo para las próximas atenciones.
                    </p>
                ) : !permitirAlta && texto.trim() && filtradas.length === 0 && (
                    <p className="atn-form-hint" style={{ marginTop: '6px', marginBottom: 0 }}>
                        No hay ninguna opción con ese nombre. Las opciones se agregan desde Datos Maestros.
                    </p>
                )}
        </div>
    );
};
